// Rate history table for one customer (used inside the customer edit form).
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useEffect } from 'react';
import { fetchJson } from '../../api.js';

// RateHistoryPanel — collapsible table of all rate history rows for a
// customer, sorted DESC by effective_from. Loaded on demand when the
// admin opens the customer edit modal.
export default function RateHistoryPanel({ canonicalName }) {
  // Collapsed by default; rows load the first time it is opened.
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // Actions: load history, change a row's effective date, delete a row (rates re-price accordingly).
  async function load() {
    if (!canonicalName) return;
    setLoading(true); setErr(null);
    try {
      const r = await fetchJson('/api/settings/customer-rates?customer=' + encodeURIComponent(canonicalName));
      setRows(r.history || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  async function editEffectiveDate(row) {
    const current = String(row.effective_from).slice(0, 10);
    const next = prompt('Change effective date for this rate row.\n\nCurrent: ' + current + '\n\nNew date (YYYY-MM-DD):', current);
    if (!next || next === current) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(next.trim())) { alert('Enter YYYY-MM-DD format.'); return; }
    setBusy(true);
    try {
      const r = await fetch('/api/settings/customer-rates?id=' + row.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ effective_from: next.trim() })
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(r.status + ' ' + t);
      }
      await load();
    } catch (e) {
      alert('Update failed: ' + e.message);
    } finally { setBusy(false); }
  }
  async function deleteRow(row) {
    if (!confirm('Delete this rate row?\n\nEffective: ' + String(row.effective_from).slice(0,10) + '\n\nAdmin-only. All loads dated on/after this date will fall back to the next-most-recent rate (or tier default).')) return;
    setBusy(true);
    try {
      const r = await fetch('/api/settings/customer-rates?id=' + row.id, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      await load();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    } finally { setBusy(false); }
  }
  useEffect(() => { if (open && rows === null) load(); }, [open, canonicalName]);
  // Screen: toggle link, then a table of every rate period (newest first).
  const fmtRate = v => v == null ? '—' : '$' + Number(v).toLocaleString();
  const fmtDate = d => d ? String(d).slice(0,10) : '—';
  return (
    <div style={{ marginTop:'0.5rem' }}>
      <button className="link" onClick={() => setOpen(!open)}
              style={{ color:'#0369a1', fontSize:'0.9em', padding:0, background:'none', border:'none', cursor:'pointer' }}>
        {open ? '▾' : '▸'} Rate history {rows && rows.length > 0 ? '(' + rows.length + ' row' + (rows.length === 1 ? '' : 's') + ')' : ''}
      </button>
      {open && (
        <div style={{ marginTop:'0.4rem', maxHeight:'18em', overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:4 }}>
          {loading && <div className="small" style={{ padding:'0.5rem' }}>Loading…</div>}
          {err && <div className="small" style={{ padding:'0.5rem', color:'#991b1b' }}>Error: {err}</div>}
          {rows && rows.length === 0 && !loading && (
            <div className="small" style={{ padding:'0.5rem', color:'#666' }}>
              No rate history yet. First save will create the initial row.
            </div>
          )}
          {rows && rows.length > 0 && (
            <table className="datatable" style={{ margin:0, fontSize:'0.83em' }}>
              <thead><tr>
                <th>Effective from</th>
                <th className="num">Small</th>
                <th className="num">Regular</th>
                <th className="num">18m³</th>
                <th className="num">28m³</th>
                <th className="num">DM</th>
                <th className="num">DM-18</th>
                <th className="num">DM-28</th>
                <th>Notes</th>
                <th>By</th>
                <th></th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td><strong>{fmtDate(r.effective_from)}</strong></td>
                    <td className="num">{fmtRate(r.small_rate)}</td>
                    <td className="num">{fmtRate(r.regular_rate)}</td>
                    <td className="num">{fmtRate(r.oversize18_rate)}</td>
                    <td className="num">{fmtRate(r.oversize28_rate)}</td>
                    <td className="num">{fmtRate(r.drill_mud_rate)}</td>
                    <td className="num">{fmtRate(r.drill_mud_18_rate)}</td>
                    <td className="num">{fmtRate(r.drill_mud_28_rate)}</td>
                    <td className="small" style={{ maxWidth:'12em', overflow:'hidden', textOverflow:'ellipsis' }}>{r.notes || ''}</td>
                    <td className="small">{r.created_by || ''}</td>
                    <td className="small" style={{ whiteSpace: 'nowrap' }}>
                      <button onClick={() => editEffectiveDate(r)} disabled={busy}
                              title="Change effective date (admin)"
                              style={{ color: '#0369a1', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✎</button>
                      <button onClick={() => deleteRow(r)} disabled={busy}
                              title="Delete this rate row (admin)"
                              style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
